import {
  ChangeDetectionStrategy, Component, computed, inject, input, model, output, signal, OnInit, OnDestroy, HostBinding, HostListener, ElementRef, NgZone,
} from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { toObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime } from 'rxjs';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { FikoYTKokBilesenComponent } from '@service/kok-bilesen/kok-bilesen.component';
import { DashboardService } from '@uygulamalar/hmi-pano/dashboard/dashboard.service';
import {
  ISablon, ISablonElement, ISablonGorunumStil, IArkaPlan,
  ISablonOlaylar, ISablonVeriKaynagi, ISablonItem,
  ElementTur, yeniSablonElement, bosISablon,
} from '../sablon-editoru/sablon.interface';
import { TePaletComponent } from './te-palet.component';
import { TeCanvasComponent } from './te-canvas.component';
import { TeKodPaneliComponent } from './te-kod-paneli.component';
import { TeStilPaneliComponent } from './te-stil-paneli.component';
import { TeVeriPaneliComponent } from './te-veri-paneli.component';
import { TeParametreTablosuComponent } from './te-parametre-tablosu.component';

@Component({
  selector: 'fiko-tasarim-editoru',
  standalone: true,
  imports: [
    CommonModule, RouterModule, FormsModule,
    NzLayoutModule, NzButtonModule, NzModalModule, NzTabsModule, NzTagModule, NzInputModule, NzTooltipModule, NzIconModule,
    TePaletComponent, TeCanvasComponent, TeKodPaneliComponent,
    TeStilPaneliComponent, TeVeriPaneliComponent, TeParametreTablosuComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './fiko-tasarim-editoru.component.html',
  styleUrl: './fiko-tasarim-editoru.component.less',
})
export class FikoTasarimEditoruComponent extends FikoYTKokBilesenComponent implements OnInit, OnDestroy {
  private konum = inject(Location);
  private dashSvc = inject(DashboardService);

  sablon = model<ISablon>();
  parametreler = input<Record<string, any>>({});
  kaydedildi = output<ISablon>();

  private elRef = inject(ElementRef);
  private zone = inject(NgZone);

  seciliElementId = signal<string | null>(null);
  kodPaneliAcik = signal(true);
  kodPaneliYukseklik = signal(200);
  tamEkran = signal(false);
  stilPaneliAcik = signal(true);

  private gecmis = signal<ISablon[]>([]);
  private gecmisIndex = signal(-1);

  mobilPaletAcik = signal(false);
  mobilStilAcik = signal(false);
  mobilKodAcik = signal(false);
  mobilVeriAcik = signal(false);
  mobilOlaylarAcik = signal(false);

  paletAcik = signal(true);

  @HostBinding('class.tam-ekran') get tamEkranClass() { return this.tamEkran(); }

  seciliElement = computed(() => {
    const id = this.seciliElementId();
    return this.sablon()?.elementler?.find(e => e.id === id) ?? null;
  });

  onizlemeSablon = signal<ISablon | null>(null);

  parametreTabloVerisi = computed<Record<string, any>[]>(() => {
    const items = this.sablon()?.itemler ?? [];
    return items.map(item => {
      const { id, sablon, veriKaynagi, olaylar, ...rest } = item as any;
      return rest;
    });
  });

  constructor() {
    super();
    toObservable(this.sablon).pipe(
      debounceTime(300),
      takeUntilDestroyed(),
    ).subscribe(s => this.onizlemeSablon.set(s ? structuredClone(s) : null));
  }

  ngOnInit() {
    if (!this.sablon()) {
      // Standalone route modu: sablon yükle ve tam ekran aç
      const widgetId = this.route.snapshot.paramMap.get('widgetId');
      if (widgetId) {
        const widget = this.dashSvc.tumWidgetler().find(w => w.id === widgetId);
        this.sablon.set(widget ? structuredClone(widget) : { ...bosISablon(), id: widgetId });
      } else {
        this.sablon.set(bosISablon());
      }
      this.tamEkran.set(true);
    } else {
      // Embedded mod: DOM hazır olduktan sonra body-append ile tam ekran aç (tüm sayfayı kapla)
      setTimeout(() => this.setTamEkran(true), 0);
    }
  }

  geriDon() {
    if (this.tamEkran()) { this.setTamEkran(false); return; }
    this.konum.back();
  }

  tamEkranToggle() { this.setTamEkran(!this.tamEkran()); }
  paletToggle() { this.paletAcik.update(v => !v); }
  kodPaneliToggle() { this.kodPaneliAcik.update(v => !v); }
  stilPaneliToggle() { this.stilPaneliAcik.update(v => !v); }

  private setTamEkran(v: boolean) {
    const el = this.elRef.nativeElement as HTMLElement;
    if (v && el.parentElement && el.parentElement !== document.body) {
      const ph = document.createElement('span');
      ph.setAttribute('data-fiko-te-ph', '');
      el.parentElement.insertBefore(ph, el);
      document.body.appendChild(el);
    } else if (!v) {
      const ph = document.querySelector('[data-fiko-te-ph]');
      if (ph?.parentElement) { ph.parentElement.insertBefore(el, ph); ph.remove(); }
    }
    this.tamEkran.set(v);
  }

  ngOnDestroy() {
    if (this.tamEkran()) { this.setTamEkran(false); }
    super.ngOnDestroy();
  }

  sablonGuncelle(patch: Partial<ISablon>) {
    const mevcut = this.sablon();
    if (mevcut) {
      const yeniGecmis = [...this.gecmis().slice(0, this.gecmisIndex() + 1), structuredClone(mevcut)].slice(-20);
      this.gecmis.set(yeniGecmis);
      this.gecmisIndex.set(yeniGecmis.length - 1);
    }
    this.sablon.update(s => ({ ...(s || bosISablon()), ...patch }));
  }

  elementGuncelle(elementId: string, patch: Partial<ISablonElement>) {
    this.sablon.update(s => {
      const cur = s || bosISablon();
      return { ...cur, elementler: (cur.elementler || []).map(e => e.id === elementId ? { ...e, ...patch } : e) };
    });
  }

  elementEkle(tur: ElementTur) {
    const yeni = yeniSablonElement(tur);
    this.sablon.update(s => {
      const cur = s || bosISablon();
      return { ...cur, elementler: [...(cur.elementler || []), yeni] };
    });
    this.seciliElementId.set(yeni.id);
  }

  elementSil(elementId: string) {
    this.sablon.update(s => ({
      ...(s || bosISablon()),
      elementler: (s?.elementler || []).filter(e => e.id !== elementId),
    }));
    if (this.seciliElementId() === elementId) this.seciliElementId.set(null);
  }

  kaydet() {
    const s = this.sablon();
    if (s) {
      this.dashSvc.sablonGuncelle(s.id, s);
      this.nzMessage.success('Widget kaydedildi');
    }
    this.kaydedildi.emit(s);
  }

  onElementSecildi(id: string | null) { this.seciliElementId.set(id); }
  onElementGuncellendi(e: { id: string; patch: Partial<ISablonElement> }) { this.elementGuncelle(e.id, e.patch); }
  onElementSilindi(id: string) { this.elementSil(id); }

  onElementStilGuncellendi(patch: Partial<ISablonElement>) {
    const id = this.seciliElementId();
    if (id) this.elementGuncelle(id, patch);
  }

  onSablonStilGuncellendi(stil: Partial<ISablonGorunumStil>) {
    this.sablonGuncelle({ stil: { ...(this.sablon()?.stil ?? {}), ...stil } });
  }

  onSablonArkaPlanGuncellendi(ap: Partial<IArkaPlan>) {
    this.sablonGuncelle({ arkaPlan: { ...(this.sablon()?.arkaPlan ?? {}), ...ap } as IArkaPlan });
  }

  onHtmlDegisti(v: string) { this.sablonGuncelle({ htmlIcerik: v }); }
  onCssDegisti(v: string) { this.sablonGuncelle({ cssIcerik: v }); }
  onJsDegisti(v: string) { this.sablonGuncelle({ jsIcerik: v }); }
  onOlaylarDegisti(o: ISablonOlaylar) { this.sablonGuncelle({ olaylar: o }); }

  onVeriKaynagiDegisti(vk: ISablonVeriKaynagi) { this.sablonGuncelle({ veriKaynagi: vk }); }
  onItemlerDegisti(items: ISablonItem[]) { this.sablonGuncelle({ itemler: items }); }

  onParametreTabloVeriGuncellendi(veriler: Record<string, any>[]) {
    const mevcutItemler = this.sablon()?.itemler ?? [];
    const yeniItemler = veriler.map((veri, i) => ({
      ...(mevcutItemler[i] ?? { id: crypto.randomUUID?.() || Date.now().toString() + i }),
      ...veri,
    }));
    this.sablonGuncelle({ itemler: yeniItemler });
  }

  geriAl() {
    const idx = this.gecmisIndex();
    if (idx < 0) return;
    this.sablon.set(structuredClone(this.gecmis()[idx]));
    this.gecmisIndex.update(v => v - 1);
  }

  ileriAl() {
    const idx = this.gecmisIndex();
    const g = this.gecmis();
    if (idx >= g.length - 1) return;
    this.gecmisIndex.update(v => v + 1);
    this.sablon.set(structuredClone(g[idx + 1]));
  }

  dividerBasla(event: MouseEvent) {
    event.preventDefault();
    const baslangicY = event.clientY;
    const baslangicH = this.kodPaneliYukseklik();
    const container = this.elRef.nativeElement as HTMLElement;

    this.zone.runOutsideAngular(() => {
      const onMouseMove = (e: MouseEvent) => {
        const fark = baslangicY - e.clientY;
        const maxH = container.clientHeight * 0.5;
        const yeniH = Math.max(100, Math.min(maxH, baslangicH + fark));
        this.kodPaneliYukseklik.set(yeniH);
      };

      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    });
  }

  @HostListener('document:keydown', ['$event'])
  onKlavye(e: KeyboardEvent) {
    const hedef = e.target as HTMLElement;
    const inputIcinde = hedef.tagName === 'INPUT' || hedef.tagName === 'TEXTAREA' || hedef.isContentEditable;

    if (e.ctrlKey && e.key === 's') {
      e.preventDefault();
      this.kaydet();
      return;
    }
    if (e.ctrlKey && e.shiftKey && (e.key === 'Z' || e.key === 'z')) {
      e.preventDefault();
      this.ileriAl();
      return;
    }
    if (e.ctrlKey && (e.key === 'Z' || e.key === 'z')) {
      e.preventDefault();
      this.geriAl();
      return;
    }
    if (!inputIcinde) {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        const id = this.seciliElementId();
        if (id) { e.preventDefault(); this.elementSil(id); }
        return;
      }
      if (e.key === 'f' || e.key === 'F') {
        e.preventDefault();
        this.tamEkranToggle();
        return;
      }
    }
    if (e.key === 'Escape') {
      if (this.seciliElementId()) { this.seciliElementId.set(null); return; }
      if (this.tamEkran()) { this.setTamEkran(false); return; }
    }
  }
}
